import * as React from 'react';
import styled from 'styled-components';
import { MathUtils, Object3D, Vector3 } from 'three';

import cameraManager from '../../../../../../../../viewer/camera-manager';
import { Poi } from '../../../../../../../../viewer/elements/poi/poi';
import { publish, SubscriptionTopic } from '../../../../../../../../viewer/messaging/pubsub';
import { SceneManager } from '../../../../../../../../viewer/scene-manager';
import { useForwardPointerEvents } from '../../../../../../../hooks/use-forward-pointer-event';
import { useNonce } from '../../../../../../../hooks/use-nonce';
import { useSubscribe } from '../../../../../../../hooks/use-subscribe';
import { Icon } from '../../../../../../icon/icon';

type Props = {
  poi: Poi;
};

/** Compute world distance and ground tilt angle (deg) from camera->POI. */
function getDistanceAndTiltDeg(poi: Object3D, camPos?: Vector3) {
  const worldPos = new Vector3();
  poi.updateWorldMatrix(true, false);
  poi.getWorldPosition(worldPos);

  if (!camPos) {
    return { distance: undefined, tiltDeg: 68 };
  } // sensible default

  const v = worldPos.clone().sub(camPos); // camera -> poi
  const dist = v.length();
  if (dist === 0) {
    return { distance: 0, tiltDeg: 0 };
  }

  // Option A: project onto XY (ground) plane
  const flatLen = Math.hypot(v.x, v.y); // √(x² + y²)
  const cos = MathUtils.clamp(flatLen / dist, 0, 1);
  const tiltRad = Math.acos(cos); // 0° = along ground, 90° = straight up/down

  return { distance: dist, tiltDeg: MathUtils.radToDeg(tiltRad) };
}

// Map tiltDeg (0..~90) → rotateX for the base (a bit more tilt at shallower angles)
function tiltDegToRotateX(tiltDeg: number) {
  const t = MathUtils.clamp(tiltDeg / 60, 0, 1);
  return 78 - (78 - 62) * t;
}

// Map distance → scale (clamped)
function distanceToScale(
  distance?: number,
  {
    sMin = 0.2, // smallest scale
    sMax = 1.0, // largest scale (at the camera / very near)
    d0 = 20, // half-impact distance (units of your scene)
    p = 1.5, // exponent: >1 sharpens near, <1 softens
  } = {},
) {
  if (!distance || !Number.isFinite(distance)) {
    return 1;
  }

  const w = 1 / (1 + Math.pow(distance / d0, p));
  const s = sMin + (sMax - sMin) * w;
  return MathUtils.clamp(s, sMin, sMax);
}

/** POI label (chat-bubble) with special styling for 360-degree panoramas. */
export const PoiLabel: React.FC<Props> = ({ poi }) => {
  const imageUrl = poi.getThumbnailResourceUrl('64x_');
  const [selected, setSelected] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);
  const [_, refresh] = useNonce();
  const isPanorama = poi.is360Poi();
  const [isPanoramaView, setIsPanoramaView] = React.useState(
    SceneManager.instance.getActiveSceneType() === 'panoramaViewer',
  );

  const handleEnter = () => {
    poi.setHover(true);
    setHovered(true);
  };
  const handleLeave = () => {
    poi.setHover(false);
    setHovered(false);
  };

  const forwardRef = useForwardPointerEvents<HTMLDivElement>(['mouseup', 'pointerup', 'click']);

  useSubscribe(SubscriptionTopic.NewSelection, (msg) => setSelected(msg.uuid === poi.uuid));
  useSubscribe(SubscriptionTopic.UnSelection, () => setSelected(false));
  useSubscribe(SubscriptionTopic.AssetChanged, (messageAsset) => {
    if (poi.assetId === messageAsset?.assetId) {
      refresh();
    }
  });

  useSubscribe(SubscriptionTopic.OnSceneChangeStarted, (message) =>
    setIsPanoramaView(message.scene === 'panoramaViewer'),
  );

  const handleSelect = () => {
    publish(SubscriptionTopic.NewSelection, poi);
    publish(SubscriptionTopic.AssetSelectedInViewer, poi);
  };

  const borderColor = selected ? '#ffffff' : '#ffdd33';

  const cameraPosition = cameraManager()
    .getActiveCamera()
    ?.getTHREECamera()
    .getWorldPosition(new Vector3());

  // Compute once per render — no per-frame update needed
  const { distance, tiltDeg } = React.useMemo(
    () => getDistanceAndTiltDeg(poi, cameraPosition),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [poi, cameraPosition, cameraPosition?.x, cameraPosition?.y, cameraPosition?.z],
  );

  const baseScale = React.useMemo(() => distanceToScale(distance), [distance]);
  const baseRotateX = React.useMemo(() => tiltDegToRotateX(tiltDeg), [tiltDeg]);
  return (
    <Root ref={forwardRef} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      {imageUrl ? (
        <Bubble $color={borderColor} onClick={handleSelect}>
          <Thumbnail
            $color={borderColor}
            $panorama={isPanorama}
            alt="POI thumbnail"
            src={imageUrl}
          />
          {isPanorama && <PanoBadge>360°</PanoBadge>}
        </Bubble>
      ) : (
        <IconBubble>
          <PoiIcon
            color={borderColor}
            icon={['fas', 'location-dot']}
            size="lg"
            onClick={handleSelect}
          />
        </IconBubble>
      )}

      {isPanoramaView && (
        <GroundWrap>
          <BaseCircle
            $ringColor={borderColor}
            $rotateX={baseRotateX}
            $scale={hovered ? baseScale * 1.2 : baseScale}
          />
        </GroundWrap>
      )}
    </Root>
  );
};

/* ---------- styled components ---------- */

const Root = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/** Gives a faux perspective so the circle reads as lying on the ground */
const GroundWrap = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;

  transform: translate(-50%, 1em);
  pointer-events: none;

  /* Enable 3D projection for the circle */
  perspective: 800px;
  transform-style: preserve-3d;
`;

// Fixed px size; visual size comes from transform scale ($scale)
const BaseCircle = styled.div<{
  $ringColor: string;
  $scale: number;
  $rotateX: number;
}>`
  width: 5em;
  height: 5em;
  border-radius: 9999px;
  box-sizing: border-box;
  border: 0.5em solid ${({ $ringColor }) => $ringColor};
  background: radial-gradient(
    closest-side,
    rgba(255, 255, 255, 0.25),
    rgba(255, 255, 255, 0.12) 60%,
    rgba(255, 255, 255, 0.04) 100%
  );
  backdrop-filter: blur(2px);

  transform: rotateX(${({ $rotateX }) => $rotateX}deg) scale(${({ $scale }) => $scale});
  transform-origin: center;
  transition: transform 160ms ease; /* hover pop is smooth */
  will-change: transform;

  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
`;

const Bubble = styled.div<{ $color: string }>`
  position: relative;
  width: 3em;
  height: 3em;
  transform: translateY(-50%);
  margin-bottom: 0.5em;
  pointer-events: auto;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    bottom: -0.45em;
    left: 50%;
    transform: translateX(-50%);
    border-width: 0.5em 0.5em 0 0.5em;
    border-style: solid;
    border-color: ${({ $color }) => $color} transparent transparent transparent;
  }
`;

const Thumbnail = styled.img<{ $color: string; $panorama?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border: 2px solid ${({ $color }) => $color};
  border-radius: ${({ $panorama }) => ($panorama ? '50%' : '10%')};
  display: block;
`;

const PanoBadge = styled.span`
  position: absolute;
  top: -0.3em;
  right: -0.3em;
  background: rgba(0, 0, 0, 0.7);
  color: #ffffff;
  font-size: 0.55rem;
  padding: 0 0.25rem;
  border-radius: 0.2rem;
  pointer-events: none;
  user-select: none;
`;

const IconBubble = styled.div`
  transform: translateY(-50%);
`;

const PoiIcon = styled(Icon)`
  pointer-events: auto;
  cursor: pointer;
`;
